<template>
  <v-menu
      v-model='isOpened'
      :close-on-content-click="false"
      :offset="4"
      >
      <template v-slot:activator="{props}">
        <v-btn
            :x-small="smAndDown"
            color="primary"
            dark
            v-bind="props"
            >
            <v-icon size="small" class="mr-1">mdi-warehouse</v-icon>
            <div>
              {{ currentStockage }}
            </div>
          <v-progress-circular
              class="ml-2"
              v-show="loading_"
              :indeterminate="true"
              size="18"
              width="2"
              ></v-progress-circular>
          <v-icon>mdi-menu-down</v-icon>
        </v-btn>
      </template>

      <v-card class="stock-menu">
        <!-- Type to search by name -->
        <div class="stock-menu__search">
          <v-text-field
            ref="search"
            v-model="search"
            density="compact"
            variant="solo-filled"
            flat
            rounded="lg"
            hide-details
            clearable
            autofocus
            prepend-inner-icon="mdi-magnify"
            :placeholder="$filters.capitalize($t('search_for', { noun: $t('stockages.label', 1) }))"
            @keydown.enter.prevent="chooseFirst"
            @keydown.esc="isOpened = false"
          ></v-text-field>
        </div>

        <v-list density="compact" class="stock-menu__list" nav>
          <v-list-item
              v-for="item in filteredStockages"
              :key="item.stockage_id"
              :active="item.stockage_id === stockage_id"
              color="primary"
              rounded="lg"
              @click="chooseStockage(item)"
              >
              <v-list-item-title>{{ item.stockage_name }}</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="!filteredStockages.length">
            <v-list-item-title class="text-medium-emphasis">{{ $t("sge.no_data") }}</v-list-item-title>
          </v-list-item>
        </v-list>
        <div class="stock-menu__foot">
          {{ filteredStockages.length }} / {{ stockages.length }} {{ $t("stockages.label", stockages.length) }}
        </div>
      </v-card>
  </v-menu>
</template>

<script>
  import { isNone } from "@/functions.js";
  import { useDisplay } from "vuetify";
  import { mapState } from "vuex";

  export default {
    props: {
      stockage_id: { type: [Number, String], required: false, default: "" },
      loading: { type: Boolean, required: false, default: false },
    },
    computed: {
      stockages(){
        let A =  Array.from(
          new Map(
            this.stockage_items?.map(x => [x.stockage_name, { stockage_name: x.stockage_name, stockage_id: x.stockage_id }])
          ).values())
        return A.sort((a, b) => String(a.stockage_name).localeCompare(String(b.stockage_name)))
      },
      // Matches the typed text against the stockage name, accents ignored
      filteredStockages() {
        const needle = this.normalize(this.search);
        if (!needle) return this.stockages;
        return this.stockages.filter((s) => this.normalize(s.stockage_name).includes(needle));
      },
      smAndDown(){
        return useDisplay().smAndDown.value
      },
      ...mapState({
        stockage_items: state => state.stockages.x_prod_type
      }),
    },

    methods: {
      normalize(text) {
        return String(text || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      },

      // Enter in the search field picks the first match
      chooseFirst() {
        if (this.filteredStockages.length) {
          this.chooseStockage(this.filteredStockages[0]);
        }
      },

      updateCurrentStockage(stockage_id) {
        // update selected stockage/producer type label
        let index = this.stockages.findIndex(
          (i) =>
          i.stockage_id === stockage_id
        );
        if (index !== -1) {
          this.currentStockage = this.stockages[index].stockage_name;
        } else {
          this.currentStockage = this.$t("actions.choose_stock");
        }
        this.producer_types = this.stockage_items.filter(x => x.stockage_name == this.currentStockage)
        // let the parent know the producer types of the current stockage,
        // also on mount / when the list arrives (no click involved)
        this.$emit("producerTypes", this.producer_types)
      },

      chooseStockage(select) {
        // on select
        this.isOpened = false;
        this.search = "";
        this.updateCurrentStockage(select.stockage_id);
        this.$emit("chooseStock", {
          stockage_id: select.stockage_id,
          // producer_type_id: select.producer_type_id,
          producer_types: this.producer_types
        });
      },
      loadStockagesIfEmpty() {
        if (this.stockage_items?.length == 0) {
          this.$store.dispatch("stockages/getXProdType").then (()=>{
            this.updateCurrentStockage(this.stockage_id);
          })
        }
      },
    },

    mounted() {
      this.loading_ = this.loading;
      this.loadStockagesIfEmpty();
      if (!isNone(this.stockage_id)) {
        this.updateCurrentStockage(this.stockage_id);
      }
    },

    watch: {
      stockage_id(newValue) {
        // stockage preset or changed by the parent (ex: from the URL)
        if (!isNone(newValue)) this.updateCurrentStockage(newValue);
      },
      loading(newValue) {
        // changed in parent
        // https://stackoverflow.com/questions/46425633/how-to-send-updated-values-from-parent-component-to-child-component-in-vue-js
        this.loading_ = newValue;
      },
      isOpened(val) {
        if (val) {
          this.loadStockagesIfEmpty();
          this.search = "";
          this.$nextTick(() => {
            const field = this.$refs.search;
            if (field && field.focus) field.focus();
          });
        }
      },
    },

    data() {
      return {
        moduleName: "stockages",
        // stockage_items: [],
        loading_: false,
        isOpened: false,
        search: "",
        currentStockage: this.$t("actions.choose_stock"),
        producer_types: [],
      };
    },
  };
</script>

<style scoped>
.stock-menu {
  width: 340px;
  max-width: 90vw;
  border-radius: 12px;
}
.stock-menu__search {
  padding: 10px 10px 6px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.stock-menu__list {
  max-height: 50vh;
  overflow-y: auto;
}
.stock-menu__foot {
  padding: 6px 14px 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 0.72rem;
  color: rgba(0, 0, 0, 0.55);
  text-align: right;
}
</style>
